import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import memoize from 'memoizee';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import MediaQuery from 'react-responsive';
import { breakpoints } from 'cityads-v3/utils/styleUtils';
import { withTranslation } from 'react-i18next';
import i18n from 'cityads-v3/i18n';
import withHooks from 'cityads-v3/decorators/withHooks'
import { useOfferTrafficTypes } from 'cityads-v3/hooks/trafficTypes'

import CircularProgress from '@mui/material/CircularProgress'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Table from 'cityads-v3/components/Table';
import TextCollapser from 'cityads-v3/components/Collapser/TextCollapser';

import {
  getTrafficTypes,
  getTrafficTypesByTypeAndCategory,
  getOfferTrafficTypes,
  getOfferCustomTrafficTypes,
} from 'cityads-v3/actions/trafficTypes';

import classes from './Traffic.scss';

import {
  TRAFFIC_TYPE_VALUE_DISALLOWED,
  TRAFFIC_TYPE_VALUE_ALLOWED
} from 'cityads-v3/reducers/trafficTypes'

const Allowed = ({children}) => (
  <div className={classes.trafficType__positive}>
    {children}
  </div>
)

const Disallowed = ({children}) => (
  <div className={classes.trafficType__negative}>
    {children}
  </div>
)

@withHooks(props => {
  const { standardOfferTrafficTypes, nonStandardOfferTrafficTypes } = useOfferTrafficTypes(props.offerId)

  return {
    ...props,
    standardOfferTrafficTypes,
    nonStandardOfferTrafficTypes,
  }
})
@connect(
  (state, props) => ({
    allTrafficTypesLoading: state.trafficTypes.isLoading,
    standardTrafficTypeIdsLoading: state.trafficTypes.trafficTypesLoadingByActionTypeDirectionIdAndCategoryId[props.actionTypeDirectionId]?.[props.categoryId],
    offerTrafficTypesLoading: state.trafficTypes.offerTrafficTypesLoadingByOfferId[props.offerId],
    offerCustomTrafficTypes       : state.trafficTypes.offerCustomTrafficTypesByOfferId[props.offerId] || [],
    offerCustomTrafficTypesLoading: state.trafficTypes.offerCustomTrafficTypesLoadingByOfferId[props.offerId],
    offerCustomTrafficTypesLoaded : state.trafficTypes.offerCustomTrafficTypesLoadedByOfferId[props.offerId],
    currentLanguage: state.language.language,
    categoryTrafficTypesLoading: state.trafficTypes.categoryTrafficTypesLoading,
  }),
  dispatch => bindActionCreators({
    getTrafficTypes,
    getTrafficTypesByTypeAndCategory,
    getOfferTrafficTypes,
    getOfferCustomTrafficTypes,
  }, dispatch)
)
@withTranslation(['Offers'], { i18n, wait: false })
export default class Traffic extends Component {

  static propTypes = {
    offerId: PropTypes.number.isRequired,
    actionTypeDirectionId: PropTypes.number.isRequired,
    categoryId: PropTypes.number.isRequired,

    allTrafficTypesLoading: PropTypes.bool,
    standardTrafficTypeIdsLoading: PropTypes.bool,
    offerTrafficTypesLoading: PropTypes.bool,
    offerCustomTrafficTypes       : PropTypes.array.isRequired,
    offerCustomTrafficTypesLoading: PropTypes.bool,
    offerCustomTrafficTypesLoaded : PropTypes.bool,
    currentLanguage: PropTypes.string,

    t: PropTypes.func.isRequired,
  }

  state = {
    rowExpandedByIndex: {},
    trafficTypeTruncatedByIndex: {},
    noteTruncatedByIndex: {},
    sortColumn: null,
    sortDirection: null,
  }

  constructor(props) {
    super(props);

    this.getOnTrafficTypeTruncate = memoize(this.getOnTrafficTypeTruncate.bind(this));
    this.getOnNoteTruncate = memoize(this.getOnNoteTruncate.bind(this));
    this.getOnRowExpandClick = memoize(this.getOnRowExpandClick.bind(this));
  }

  componentDidMount() {
    const {
      offerId,

      offerCustomTrafficTypesLoading,
      offerCustomTrafficTypesLoaded,
      getOfferCustomTrafficTypes,
    } = this.props;

    if (!offerCustomTrafficTypesLoading && !offerCustomTrafficTypesLoaded) {
      getOfferCustomTrafficTypes(offerId);
    }

  }

  componentDidUpdate(prevProps) {
    const {
      currentLanguage,
      offerId,
      actionTypeDirectionId,
      categoryId,

      getTrafficTypes,
      getTrafficTypesByTypeAndCategory,
      getOfferTrafficTypes,
      getOfferCustomTrafficTypes,
    } = this.props;

    if (currentLanguage !== prevProps.currentLanguage) {
      getTrafficTypes();
    }

    if (currentLanguage !== prevProps.currentLanguage || actionTypeDirectionId !== prevProps.actionTypeDirectionId || categoryId !== prevProps.categoryId) {
      getTrafficTypesByTypeAndCategory(actionTypeDirectionId, categoryId);
    }

    if (currentLanguage !== prevProps.currentLanguage || offerId !== prevProps.offerId) {
      getOfferTrafficTypes(offerId);
      getOfferCustomTrafficTypes(offerId);
    }
  }

  renderTrafficTypeText(value, trafficTypeName, hint) {
    const text = hint ? <Tooltip title={hint}><span>{trafficTypeName}</span></Tooltip> : trafficTypeName;

    switch (value) {
      case TRAFFIC_TYPE_VALUE_DISALLOWED:
        return <Disallowed>{text}</Disallowed>

      case TRAFFIC_TYPE_VALUE_ALLOWED:
        return <Allowed>{text}</Allowed>

      default:
        return {text}
    }
  }

  renderTrafficTypeValue(value, trafficTypeName, hint, index) {
    return (
      <TextCollapser
        lines = {2}
        expanded = {this.state.rowExpandedByIndex[index]}
        onTruncate = {this.getOnTrafficTypeTruncate(index)}
      >
        {this.renderTrafficTypeText(value, trafficTypeName, hint)}
      </TextCollapser>
    );
  }

  getOnTrafficTypeTruncate(index) {
    return (truncated) => {
      if (this.state.trafficTypeTruncatedByIndex[index] === truncated) { return; }

      this.setState(({ trafficTypeTruncatedByIndex }) => ({
        trafficTypeTruncatedByIndex: {
          ...trafficTypeTruncatedByIndex,
          [index]: truncated,
        }
      }));
    };
  }

  renderNote(text, index) {
    return (
      <div className={classes.noteText}>
        <TextCollapser
          lines = {2}
          expanded = {this.state.rowExpandedByIndex[index]}
          onTruncate = {this.getOnNoteTruncate(index)}
        >
          {text}
        </TextCollapser>
      </div>
    );
  }

  getOnNoteTruncate(index) {
    return (truncated) => {
      if (this.state.noteTruncatedByIndex[index] === truncated) { return; }

      this.setState(({ noteTruncatedByIndex }) => ({
        noteTruncatedByIndex: {
          ...noteTruncatedByIndex,
          [index]: truncated,
        }
      }));
    };
  }

  getOnRowExpandClick(index) {
    return (evt) => {
      this.setState(({ rowExpandedByIndex, trafficTypeTruncatedByIndex, noteTruncatedByIndex }) => ({
        rowExpandedByIndex: {
          ...rowExpandedByIndex,
          [index]: !rowExpandedByIndex[index],
        },

        // if collapser was expanded, set that it is now truncated right away to avoid glitching of collapser button
        trafficTypeTruncatedByIndex: {
          ...trafficTypeTruncatedByIndex,
          [index]: rowExpandedByIndex[index] ? true : rowExpandedByIndex[index],
        },
        noteTruncatedByIndex: {
          ...noteTruncatedByIndex,
          [index]: rowExpandedByIndex[index] ? true : rowExpandedByIndex[index],
        },
      }));
    };
  }

  getTableCols(isWide) {
    const { t } = this.props;

    return [
      {
        name: 'index',
        title: '#',
        classes: { root: classes.numberCell },
        headClasses: { root: classes.numberHeaderCell },
        formatter: value => value >= 10 ? value : '0' + value
      },
      {
        name: 'name',
        title: t('trafficType'),
        classes: { root: classes.typeCell },
        headClasses: { root: classes.typeHeaderCell },
        sortable: true,
        formatter: (value, row) => (
          <Fragment>
            {this.renderTrafficTypeValue(Number(row.value), row.name, row.hint, row.index)}
            {isWide ?
              null :
              this.renderNote(row.note || '', row.index)
            }
          </Fragment>
        )
      },
      isWide && {
        name: 'note',
        title: t('note'),
        classes: { root: classes.noteCell },
        headClasses: { root: classes.noteHeaderCell },
        formatter: (value, row) => this.renderNote(value || '', row.index)
      },
      {
        name: 'expand',
        title: '',
        classes: { root: classes.expandCell },
        headClasses: { root: classes.expandHeaderCell },
        formatter: (value, row) => (
          (this.state.trafficTypeTruncatedByIndex[row.index] || this.state.noteTruncatedByIndex[row.index] || this.state.rowExpandedByIndex[row.index]) ?
            (
              <IconButton
                className = {classNames(classes.expandButton, { [classes.expandButtonExpanded]: this.state.rowExpandedByIndex[row.index] })}
                onClick={this.getOnRowExpandClick(row.index)}
                size="large"
              >
                <ExpandMoreIcon />
              </IconButton>
            ) :
            null
        )
      }
    ];
  }

  onRequestSort = (evt, column) => {
    this.setState((state, props) => {
      if (state.sortColumn === column) {
        switch (state.sortDirection) {
          case 'asc':
            return { sortDirection: 'desc' };
          case 'desc':
            return { sortColumn: null, sortDirection: null };
          default:
            return { sortDirection: 'asc' };
        }
      } else {
        return {
          sortColumn: column,
          sortDirection: 'asc'
        };
      }
    });
  }

  getSortedRows() {
    const {
      standardOfferTrafficTypes,
      nonStandardOfferTrafficTypes,
      offerCustomTrafficTypes,
      currentLanguage,
    } = this.props;

    const rows = [
      ...standardOfferTrafficTypes,
      ...offerCustomTrafficTypes.map(octt => ({
        name: octt.name,
        value: octt.value,
        note: octt.note,
      })),
      ...nonStandardOfferTrafficTypes,
    ];

    const { sortColumn, sortDirection } = this.state;

    switch (sortColumn) {
      case 'name':
        switch (sortDirection) {
          case 'asc':
            rows.sort((item1, item2) => (item2.value - item1.value) || item1.name.localeCompare(item2.name, currentLanguage))
            break;
          case 'desc':
            rows.sort((item1, item2) => (item1.value - item2.value) || item1.name.localeCompare(item2.name, currentLanguage))
            break;
          default:
            // unknown way to sort
            break;
        }
        break;
      default:
        // unknown way to sort
        break;
    }

    return rows.map((item, index) => ({
      ...item,
      index: index + 1,
    }));
  }

  render() {
    const {
      allTrafficTypesLoading,
      standardTrafficTypeIdsLoading,
      offerTrafficTypesLoading,
      offerCustomTrafficTypesLoading,
      categoryTrafficTypesLoading,
      categoryId,
    } = this.props;

    if (allTrafficTypesLoading
      || standardTrafficTypeIdsLoading
      || offerTrafficTypesLoading
      || offerCustomTrafficTypesLoading
      || categoryTrafficTypesLoading[categoryId]
    ) {
      return (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      );
    }

    const sortedRows = this.getSortedRows();

    const { sortColumn, sortDirection } = this.state;

    return (
      <div>
        <MediaQuery minWidth={parseInt(breakpoints.sm, 10)}>
          {(matches) => (
            <div className={classes.trafficTableContainer} data-traffic="table">
              <Table
                className={classes.trafficTable}
                cols={this.getTableCols(!!matches)}
                rows={sortedRows}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onRequestSort={this.onRequestSort}
                showRowHover={true}
                blueTheme={true}
                scrollX={true}
              />
            </div>
          )}
        </MediaQuery>
      </div>
    );
  }

}
